import React, { useCallback, useMemo } from "react";
import { connect } from "react-redux";

import { useIntl } from "react-intl";

import ActivityVisibility from "@mapmycustomers/shared/enum/activity/ActivityVisibility";
import ActivityType from "@mapmycustomers/shared/types/entity/activities/ActivityType";
import Activity from "@mapmycustomers/shared/types/entity/Activity";
import FormLayout from "@mapmycustomers/shared/types/layout/FormLayout";
import Team from "@mapmycustomers/shared/types/Team";
import User, { UserRef } from "@mapmycustomers/shared/types/User";
import { OnlyRequiredFields } from "@mapmycustomers/shared/util/ts";
import { Button, Labeled } from "@mapmycustomers/ui";

import AssigneeSearch from "@app/component/activity/AssigneeSearch";
import SelectActivityTypeField from "@app/component/input/SelectActivityTypeField";
import VisibilitySelectField from "@app/component/input/VisibilitySelectField";
import {
  bulkEdit,
  changeActivityAssignee,
  changeActivityType,
  changeActivityVisibility,
  changeActivityVisibilityTeamsIds,
} from "@app/scene/activity/store/actions";
import {
  getActivityAssignee,
  getActivityType,
  getActivityVisibility,
  getActivityVisibilityTeamIds,
  getBulkEditLoading,
} from "@app/scene/activity/store/selectors";
import { isCurrentUserManager, isCurrentUserMember, isCurrentUserOwner } from "@app/store/iam";
import { getTeams, getUsers } from "@app/store/members";
import { RootState } from "@app/store/rootReducer";
import useActivityVisibilityOptions from "@app/util/hook/useActivityVisibilityOptions";
import { activityLayoutModel } from "@app/util/layout/impl";
import { getActivityVisibilityNote } from "@app/util/ui";

import styles from "./BulkEditPopoverContent.module.scss";

interface Props {
  activityAssignee?: UserRef;
  activityType?: ActivityType;
  activityTypes: ActivityType[];
  activityVisibility?: ActivityVisibility;
  activityVisibilityTeamsIds?: Team["id"][];
  bulkEdit: typeof bulkEdit.request;
  changeActivityAssignee: typeof changeActivityAssignee;
  changeActivityType: typeof changeActivityType;
  changeActivityVisibility: typeof changeActivityVisibility;
  changeActivityVisibilityTeamsIds: typeof changeActivityVisibilityTeamsIds;
  hidePopover: () => void;
  isBulkEditLoading: boolean;
  isManager: boolean;
  isMember: boolean;
  isOwner: boolean;
  selected: Activity[];
  teams: Team[];
  users: User[];
}

const BulkEditPopoverContent: React.FC<Props> = ({
  activityAssignee,
  activityType,
  activityTypes,
  activityVisibility,
  activityVisibilityTeamsIds,
  bulkEdit,
  changeActivityAssignee,
  changeActivityType,
  changeActivityVisibility,
  changeActivityVisibilityTeamsIds,
  hidePopover,
  isBulkEditLoading,
  isManager,
  isMember,
  isOwner,
  selected,
  teams,
  users,
}) => {
  const intl = useIntl();
  const visibilityOptions = useActivityVisibilityOptions(isOwner, isManager);
  const handleActivityTypeChange = useCallback(
    (id: Activity["id"]) => {
      changeActivityType(activityTypes.find((item) => item.id === id));
    },
    [activityTypes, changeActivityType]
  );

  const handleSubmit = useCallback(() => {
    bulkEdit({
      activities: selected.map((activity) => {
        const updated: OnlyRequiredFields<Activity, "id"> & { layoutId: FormLayout["id"] } = {
          id: activity.id,
          layoutId: activityLayoutModel.getLayoutFor(activity).id,
        };

        if (activityAssignee) {
          updated.assignee = activityAssignee;
        }
        if (activityType) {
          updated.crmActivityType = activityType;
        }
        if (activityVisibility) {
          updated.visibility = activityVisibility;
        }
        if (activityVisibilityTeamsIds) {
          updated.teamId = activityVisibilityTeamsIds;
        }

        return updated;
      }),
      onSuccess: hidePopover,
    });
  }, [
    activityAssignee,
    activityType,
    activityVisibility,
    activityVisibilityTeamsIds,
    bulkEdit,
    hidePopover,
    selected,
  ]);
  const submitDisabled = useMemo(
    () => !activityAssignee && !activityType && !activityVisibility,
    [activityAssignee, activityType, activityVisibility]
  );

  return (
    <div className={styles.popoverContentContainer}>
      <Labeled
        className={styles.control}
        label={intl.formatMessage({
          id: "activity.listView.bulkEdit.activityAssignee",
          defaultMessage: "Activity Assignee",
          description: "Label for activity Assignee select for bulk edit",
        })}
      >
        <AssigneeSearch onChange={changeActivityAssignee} users={users} />
      </Labeled>
      <Labeled
        className={styles.control}
        label={intl.formatMessage({
          id: "activity.listView.bulkEdit.activityType",
          defaultMessage: "Activity Type",
          description: "Label for activity type select for bulk edit",
        })}
      >
        <SelectActivityTypeField
          className={styles.select}
          onChange={handleActivityTypeChange}
          value={activityType?.id}
        />
      </Labeled>
      <Labeled
        className={styles.control}
        label={intl.formatMessage({
          id: "activity.listView.bulkEdit.activityVisibility",
          defaultMessage: "Activity Visibility",
          description: "Label for activity visibility select for bulk edit",
        })}
      >
        <VisibilitySelectField<ActivityVisibility>
          className={styles.visibility}
          disabled={isMember}
          noteGetter={getActivityVisibilityNote}
          onChange={changeActivityVisibility}
          onTeamIdsChange={(ids) => changeActivityVisibilityTeamsIds(ids)}
          options={visibilityOptions}
          teamIds={activityVisibilityTeamsIds}
          teams={teams}
          value={isMember ? ActivityVisibility.PRIVATE : activityVisibility}
        />
      </Labeled>
      <div className={styles.popoverButtons}>
        <Button onClick={hidePopover}>
          {intl.formatMessage({
            id: "activity.listView.bulkEdit.cancel",
            defaultMessage: "Cancel",
            description: "Label for bulk edit cancel button",
          })}
        </Button>
        <Button
          disabled={submitDisabled || isBulkEditLoading}
          loading={isBulkEditLoading}
          onClick={handleSubmit}
          type="primary"
        >
          {intl.formatMessage(
            {
              id: "activity.listView.bulkEdit.submit",
              defaultMessage: "Update {count} {count, plural, one {activity} other {activities}}",
              description: "Label for bulk edit submit button",
            },
            {
              count: selected.length,
            }
          )}
        </Button>
      </div>
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  activityAssignee: getActivityAssignee(state),
  activityType: getActivityType(state),
  activityVisibility: getActivityVisibility(state),
  activityVisibilityTeamsIds: getActivityVisibilityTeamIds(state),
  isBulkEditLoading: getBulkEditLoading(state),
  isManager: isCurrentUserManager(state),
  isMember: isCurrentUserMember(state),
  isOwner: isCurrentUserOwner(state),
  teams: getTeams(state),
  users: getUsers(state),
});

const mapDispatchToProps = {
  bulkEdit: bulkEdit.request,
  changeActivityAssignee,
  changeActivityType,
  changeActivityVisibility,
  changeActivityVisibilityTeamsIds,
};

export default connect(mapStateToProps, mapDispatchToProps)(BulkEditPopoverContent);
